import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { FiDatabase, FiUpload, FiDownload, FiSearch, FiMapPin } from 'react-icons/fi';
import toast from 'react-hot-toast';
import api from '../services/api';

const COLORS = ['#3b82f6', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];

const PopulationData = () => {
  const { canCreate, canEdit, isAdmin } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.get('/population');
      const result = res.data.data || res.data.records || res.data || [];
      setData(Array.isArray(result) ? result : []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleExport = async () => {
    try {
      const csvContent = [
        ['Region', 'Year', 'Population', 'Life Expectancy', 'Birth Rate', 'Death Rate', 'Infant Mortality'].join(','),
        ...data.map(d => [
          d.region, d.year, d.totalPopulation,
          d.healthMetrics?.lifeExpectancy?.toFixed(1),
          d.healthMetrics?.birthRate?.toFixed(1),
          d.healthMetrics?.deathRate?.toFixed(1),
          d.healthMetrics?.infantMortalityRate?.toFixed(1)
        ].join(','))
      ].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'population_data.csv'; a.click();
      toast.success('Data exported');
    } catch { toast.error('Export failed'); }
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><div style={{ width: 40, height: 40, border: '4px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>;

  const filtered = data.filter(d => d.region?.toLowerCase().includes(searchTerm.toLowerCase()));
  const selected = selectedRegion ? data.find(d => d.region === selectedRegion) : null;

  const popData = filtered.map(d => ({ name: d.region, population: d.totalPopulation }));
  const healthData = filtered.map(d => ({
    name: d.region,
    lifeExpectancy: d.healthMetrics?.lifeExpectancy?.toFixed(1),
    birthRate: d.healthMetrics?.birthRate?.toFixed(1),
    deathRate: d.healthMetrics?.deathRate?.toFixed(1),
  }));
  const insuranceData = selected ? [
    { name: 'Private', value: selected.insuranceCoverage?.private || 0 },
    { name: 'Medicaid', value: selected.insuranceCoverage?.medicaid || 0 },
    { name: 'Medicare', value: selected.insuranceCoverage?.medicare || 0 },
    { name: 'Uninsured', value: selected.insuranceCoverage?.uninsured || 0 },
  ] : [];

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 className="page-title" style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>Population Data Management</h1>
          <p style={{ color: '#64748b', marginTop: 4 }}>Demographic data, health metrics, and data import/export</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {canEdit && <button onClick={handleExport} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
            <FiDownload size={16} /> Export CSV
          </button>}
        </div>
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 24, maxWidth: 400 }}>
        <FiSearch size={18} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
        <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search regions..."
          style={{ width: '100%', padding: '10px 14px 10px 42px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
      </div>

      {/* Charts */}
      <div className="chart-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Population by Region</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={popData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={v => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : `${(v / 1000).toFixed(0)}K`} />
              <Tooltip formatter={v => v.toLocaleString()} />
              <Bar dataKey="population" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Health Metrics by Region</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={healthData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="lifeExpectancy" fill="#10b981" radius={[4, 4, 0, 0]} name="Life Expect." />
              <Bar dataKey="birthRate" fill="#ec4899" radius={[4, 4, 0, 0]} name="Birth Rate" />
              <Bar dataKey="deathRate" fill="#64748b" radius={[4, 4, 0, 0]} name="Death Rate" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Region Cards */}
      <div className="stat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 24 }}>
        {filtered.map(d => (
          <div key={d._id} onClick={() => setSelectedRegion(d.region)}
            style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', cursor: 'pointer', transition: 'all 0.2s',
              border: selectedRegion === d.region ? '2px solid #3b82f6' : '1px solid #f1f5f9' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: '#3b82f615', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FiMapPin size={20} color="#3b82f6" />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>{d.region}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>Year: {d.year}</div>
              </div>
              <div style={{ marginLeft: 'auto', fontSize: 20, fontWeight: 800, color: '#3b82f6' }}>
                {d.totalPopulation?.toLocaleString()}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
              {[
                { label: 'Life Expectancy', value: `${d.healthMetrics?.lifeExpectancy?.toFixed(1)} yrs`, color: '#10b981' },
                { label: 'Birth Rate', value: d.healthMetrics?.birthRate?.toFixed(1), color: '#ec4899' },
                { label: 'Death Rate', value: d.healthMetrics?.deathRate?.toFixed(1), color: '#64748b' },
                { label: 'Infant Mortality', value: d.healthMetrics?.infantMortalityRate?.toFixed(1), color: '#f59e0b' },
              ].map((m, i) => (
                <div key={i} style={{ padding: '8px 10px', background: '#f8fafc', borderRadius: 8 }}>
                  <div style={{ fontSize: 11, color: '#64748b' }}>{m.label}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: m.color }}>{m.value || '—'}</div>
                </div>
              ))}
            </div>

            {/* Disease Prevalence */}
            {d.healthMetrics?.prevalence?.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>Disease Prevalence</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {d.healthMetrics.prevalence.map((p, j) => (
                    <span key={j} style={{ padding: '3px 8px', borderRadius: 6, background: '#f1f5f9', fontSize: 11 }}>
                      {p.condition}: <strong>{p.rate?.toFixed(1)}%</strong>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Selected Region Detail */}
      {selected && (
        <div className="chart-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Insurance Coverage — {selected.region}</h3>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={insuranceData} cx="50%" cy="50%" innerRadius={50} outerRadius={100} paddingAngle={3} dataKey="value" label={({ name, value }) => `${name}: ${value?.toFixed(1)}%`}>
                  {insuranceData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Top Causes of Death — {selected.region}</h3>
            <div style={{ display: 'grid', gap: 8 }}>
              {(selected.healthMetrics?.topCausesOfDeath || []).map((c, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#f8fafc', borderRadius: 10 }}>
                  <div>
                    <span style={{ fontWeight: 700, fontSize: 14, marginRight: 8 }}>#{i + 1}</span>
                    <span style={{ fontSize: 14 }}>{c.cause}</span>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>{c.count?.toLocaleString()}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>Rate: {c.rate?.toFixed(1)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PopulationData;
